import { AlertColor } from "@mui/material";
import classNames from "classnames";
import {
  useCallback,
  useContext,
  useState,
  useLayoutEffect,
  useMemo,
} from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ampli } from "../../../ampli";
import Modal from "../../../components/common/Modal";
import PrimaryButton from "../../../components/common/PrimaryButton/PrimaryButton";
import BackButton from "../../../components/contract/backButton";
// components
import Gallery from "../../../components/contract/gallery";
import CustomizedSnackbars from "../../../components/customizedSnackbars";
import { MainDetailsHeading } from "../contractDetails/mainDetails/MainDetailsHeadding";

// styles
import styles from "./styles.module.scss";

// context and providers
import { ContractContext } from "../../../providers/contract";
import Tabs from "../../../components/contract/tabs/tabs";
import Milestones from "../../../components/contract/milestones";
import ContractParties from "../../../components/contract/contractParties/ContractParties";
import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";
import { ECONTRACT_STATUS } from "../../../enums";
import { IUser } from "../../../setup/api/services/auth/models";
import { DialogModal } from "../../../components/common/dialog";
import { CONFIRM } from "../../authentication/constants";
import { ConfirmContractForm } from "../actions/forms/confirmContractActionForm";
import {
  ACTION_UNDER_PROCESSING,
  CANNOT_MAKE_THIS_CALL,
  CONFIRMATION_MODAL_DESCRIPTION,
} from "../constants";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import { doAction } from "../../../setup/api/services/contractActions";
import {
  IContractActionRequestBody,
  IContractActionResponse,
} from "../../../setup/api/services/contractActions/models";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { ROUTES } from "../../../setup/constants";

export interface IRightSection {
  milestones: IContractDetailsResponseData[];
  backHandler: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined
  ) => void;
}

const RightSection: React.FC<IRightSection> = ({
  backHandler,
  milestones,
}: IRightSection): JSX.Element => {
  const { contract } = useContext(ContractContext);
  const userId = AuthConfig.getUserInfo()?.id;

  const [displayDetails, setDisplayDetails] = useState(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // alerts
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");
  const [tabs, setTabs] = useState<
    { title: string | undefined; hash: string }[]
  >([]);
  const [copied, setCopied] = useState(false);

  const url = location.origin;
  const contractUrl = useMemo(() => {
    return `${url}/contract/${contract?.id}`;
  }, [contract?.id, url]);

  const onClose = () => {
    setShowModal(false);
    window.location.reload();
  };

  const onCancel = useCallback(() => {
    setSnackBarTitle("");
    setSnackBarColor(undefined);
    setSnackBarOpen(false);
  }, []);

  const contractParties = useMemo(() => {
    return milestones?.reduce(
      (acc: Partial<UserProfile & { type: ESenderRole }>[], curr) => {
        const resultArray = [...acc];
        for (const [role, parties] of Object.entries(
          curr?.partiesProfiles || []
        )) {
          parties?.forEach((party) => {
            if (
              !resultArray?.some(
                (result) => result?.phoneNumber === party.phoneNumber
              )
            )
              resultArray.push({ ...party, type: role as ESenderRole });
          });
        }

        resultArray?.forEach(function (party, index) {
          if (party?.phoneNumber === userInfo?.phoneNumber) {
            resultArray?.splice(index, 1);
            resultArray?.unshift(party);
          }
        });

        return resultArray;
      },
      []
    );
  }, [milestones, userInfo?.phoneNumber]);

  const onTabClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Set the hash
    window.location.hash = (e?.target as unknown as { hash: string })?.hash;

    // Scroll to the section + 1 to account for weird bug.
    // remove the `+1` and click on Section 2 link to see the bug.
    const targetSection = document.querySelector(
      `${(e?.target as unknown as { hash: string })?.hash}`
    );
    window.scrollTo(
      0,
      (targetSection as unknown as { offsetTop: number })?.offsetTop + 1
    );
  };

  useLayoutEffect(() => {
    const tabsSections = document.querySelectorAll("section");
    const tabsFromSections = Array.from(tabsSections).map((section) => {
      return {
        hash: section.id,
        title: section.dataset.navTitle,
      };
    });
    setTabs(tabsFromSections);
  }, []);

  const contractDetails = useCallback(() => {
    return (
      <>
        {contract?.itemDetail?.description && (
          <div className={styles.text_wrapper}>
            <p className={classNames(styles["tab_p1"], styles["g900"])}>
              {contract?.itemDetail?.description}
            </p>
          </div>
        )}
        <Tabs tabs={tabs} tabHandler={onTabClick} />
        <section
          id="section1"
          data-nav-title={t("lbl_transaction_parties")}
          data-scrollspy>
          <ContractParties defaultParties={contractParties} />
        </section>
        <hr style={{ borderTop: "1px solid #f3f4f5" }} />
        <section
          id="section2"
          data-nav-title={t("PRICE_INSTALLMENTS")}
          data-scrollspy>
          <Milestones milestones={milestones} />
        </section>
        <hr style={{ borderTop: "1px solid #f3f4f5" }} />
        <section
          id="section3"
          data-nav-title={t("lbl_attachments")}
          data-scrollspy>
          {contract?.itemDetail?.images && (
            <Gallery
              images={contract?.itemDetail?.images}
              isBrokerContract={true}
            />
          )}
        </section>
      </>
    );
  }, [
    contract?.itemDetail?.description,
    contract?.itemDetail?.images,
    milestones,
    contractParties,
    t,
    tabs,
  ]);

  const setSnackBarStates = useCallback((title: string, color: AlertColor) => {
    setSnackBarOpen(true);
    setSnackBarColor(color);
    setSnackBarTitle(title);
  }, []);

  const onDeleteClick = () => {
    setShowModal(true);
  };

  const onConfirmDelete = async () => {
    const contractActionBody: IContractActionRequestBody = {
      contractId: contract?.id || "",
      userId: userId,
      actorRole: ESenderRole.ADMIN,
      receiverId: contract?.receiverId,
      senderId: contract?.senderId,
      contractAction: EContractActions.ADMIN_DELETE_CONTRACT,
    };
    try {
      const normalRes = await doAction(contractActionBody);
      doContractAction(normalRes);
    } catch (error) {
      setSnackBarStates(CANNOT_MAKE_THIS_CALL, "error");
      doContractAction(undefined);
    }
  };

  const doContractAction = async (res?: IContractActionResponse) => {
    if (res) {
      setSnackBarStates(ACTION_UNDER_PROCESSING, "success");
      navigate(ROUTES.CONTRACTS_LIST);
    } else {
      setSnackBarStates(CANNOT_MAKE_THIS_CALL, "error");
    }
    onCancelDelete();
  };

  const onCancelDelete = () => {
    setShowModal(false);
  };

  const renderDeleteModal = () => {
    return (
      <DialogModal
        title={CONFIRM}
        open={showModal}
        handleClose={async () => {
          onCancelDelete();
        }}>
        {contract && (
          <ConfirmContractForm
            contract={contract}
            onConfirm={onConfirmDelete}
            onCancel={onCancelDelete}
            withReferenceInput={false}
            description={CONFIRMATION_MODAL_DESCRIPTION.ADMIN_DELETE_ITEM}
          />
        )}
      </DialogModal>
    );
  };

  const toggleSuccessModal = useCallback(() => {
    setShowSuccessModal((prevState) => !prevState);
  }, []);

  const onCopy = async () => {
    try {
      await navigator?.clipboard?.writeText(contractUrl);
      setCopied(true);
      ampli.sharedContract({
        shareContractPageSource: "contractDetailsPage", // enum,
        shareContractMethod: "Others", // enum,
        contractId: contract?.shortId || "",
        // TODO: Check this value for complex contract details
        // contractType: "General",
      });
      window.setTimeout(() => setCopied(false), 5000);
    } catch (error) {
      console.error(error);
    }
  };

  const renderSnackBar = useCallback(() => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  }, [snackBarColor, snackBarOpen, snackBarTitle]);

  const navigateToContract = useCallback(async () => {
    navigate(`/contract/${contractId}`);
    setShowMenu(false);
    setShowSuccessModal(false);
  }, [contractId, navigate]);

  const renderSuccess = useCallback(() => {
    return (
      <Modal
        visible={showSuccessModal}
        onCancel={toggleSuccessModal}
        size="small">
        <div className={classNames(styles.center, styles.modalContainer)}>
          <img alt="success" src="/assets/images/svg/Success.svg" />
          <h2>{t("lbl_transaction_copied_successfully")}</h2>
          <PrimaryButton onClick={navigateToContract}>
            <h3 className={styles["white"]}>{t("lbl_view_the_transaction")}</h3>
          </PrimaryButton>
        </div>
      </Modal>
    );
  }, [navigateToContract, showSuccessModal, t, toggleSuccessModal]);

  if (!contract) return <></>;

  return (
    <>
      <div className={classNames(styles.right_container)}>
        <div className={styles.top_section}>
          <div className={styles.top_section_right}>
            <BackButton onClick={backHandler} />
          </div>

          {contract?.status === ECONTRACT_STATUS.CREATED && (
            <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
              <PrimaryButton
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  height: "40px",
                  backgroundColor: copied ? "#44C16E" : "",
                }}
                onClick={onCopy}>
                {copied ? t("copied_successfully") : t("lbl_share_transaction")}
              </PrimaryButton>

              <div
                className={classNames(
                  styles["action_icon"],
                  styles["edit_icon"]
                )}>
                <img
                  alt="edit"
                  src="/assets/images/svg/editIcon.svg"
                  width="16px"
                  height="16px"
                />
              </div>

              <div
                onClick={onDeleteClick}
                className={classNames(
                  styles["action_icon"],
                  styles["delete_icon"]
                )}>
                <img
                  alt="delete"
                  src="/assets/images/svg/deleteIcon.svg"
                  width="16px"
                  height="16px"
                />
              </div>
            </div>
          )}
        </div>
        <div className={styles.heading_wrapper}>
          <MainDetailsHeading contract={contract} />
        </div>
        <div className={styles.contract_details_desktop}>
          {contractDetails()}
        </div>
        <div className={styles.contract_details_mobile}>
          {displayDetails ? (
            <>{contractDetails()}</>
          ) : (
            <div
              onClick={() => setDisplayDetails(!displayDetails)}
              className={styles.hideDetails}>
              <p className={classNames("subtitle2", "primary")}>
                {t("lbl_hide_link")}
              </p>
            </div>
          )}
        </div>
      </div>
      {renderSuccess()}
      {renderSnackBar()}
      {renderDeleteModal()}
    </>
  );
};

export default RightSection;
